import { Action, Dispatch } from 'redux';
import {
  AppState,
  getTicket,
  refreshCollection,
  toggleTicketCheck,
  deleteUpdateTicketDraft,
} from 'reducers';

import { COLLECTIONS, MODAL_STATES, TICKETS_URL } from 'appConstants';
import { ApiRequest } from 'services';
import { Ticket, TicketArray } from 'types';

/* Types */

export interface BulkCloseAction extends Action {
  readonly step?: string;
  readonly ticketId?: string;
  readonly tickets?: TicketArray;
  readonly visibility?: boolean;
}

export interface BulkClose {
  readonly _isBulkCloseVisible: boolean;
  readonly _isCurrentTicketClose: boolean;
  readonly _modalState: string;
  readonly _totalClosed: number;
  readonly tickets: TicketArray;
}

/* Initial State */

const initialState = {
  _isBulkCloseVisible: false,
  _isCurrentTicketClose: false,
  _modalState: MODAL_STATES.INITIAL,
  _totalClosed: 0,
  tickets: [],
} as BulkClose;

/* Actions */

enum Actions {
  REMOVE_TICKET = 'REMOVE_TICKET',
  RESET_CLOSE = 'RESET_CLOSE',
  SET_CURRENT_TICKET_CLOSE = 'SET_CURRENT_TICKET_CLOSE',
  SET_BULK_CLOSE_TICKETS = 'SET_BULK_CLOSE_TICKETS',
  SET_BULK_CLOSE_VISIBLE = 'SET_BULK_CLOSE_VISIBLE',
  SET_MODAL_STEP = 'SET_MODAL_STEP',
  BULK_CLOSE_START = 'BULK_CLOSE_START',
  BULK_CLOSE_ERROR = 'BULK_CLOSE_ERROR',
  BULK_CLOSE_SUCCESS = 'BULK_CLOSE_SUCCESS',
}

const removeTicket = (ticketId: string): BulkCloseAction => ({
  type: Actions.REMOVE_TICKET,
  ticketId,
});

const resetBulkClose = (): BulkCloseAction => ({
  type: Actions.RESET_CLOSE,
});

const showBulkCloseModal = (visibility: boolean): BulkCloseAction => ({
  type: Actions.SET_BULK_CLOSE_VISIBLE,
  visibility,
});

const setCurrentTicketClose = (tickets: TicketArray): BulkCloseAction => ({
  type: Actions.SET_CURRENT_TICKET_CLOSE,
  tickets,
});

const setModalStep = (step: string): BulkCloseAction => ({
  type: Actions.SET_MODAL_STEP,
  step,
});

const setTicketClosing = (): BulkCloseAction => ({
  type: Actions.BULK_CLOSE_START,
});

const setTicketClosingError = (ticketId: string): BulkCloseAction => ({
  type: Actions.BULK_CLOSE_ERROR,
  ticketId,
});

const setTicketClosingSuccess = (ticketId: string): BulkCloseAction => ({
  type: Actions.BULK_CLOSE_SUCCESS,
  ticketId,
});

const setBulkCloseTickets = (tickets: TicketArray): BulkCloseAction => ({
  type: Actions.SET_BULK_CLOSE_TICKETS,
  tickets,
});

const bulkCloseReducer = (state = initialState, action: BulkCloseAction) => {
  switch (action.type) {
    case Actions.REMOVE_TICKET:
      let filteredTickets = state.tickets.filter(
        (ticket: Ticket) => ticket.ticketId !== action.ticketId,
      );

      return {
        ...state,
        _isBulkCloseVisible: filteredTickets.length > 0,
        tickets: filteredTickets,
      };
    case Actions.RESET_CLOSE:
      return initialState;
    case Actions.SET_BULK_CLOSE_VISIBLE:
      return {
        ...state,
        _isBulkCloseVisible: action.visibility,
      };
    case Actions.SET_BULK_CLOSE_TICKETS:
      return {
        ...state,
        _isCurrentTicketClose: false,
        tickets: action.tickets,
      };
    case Actions.SET_CURRENT_TICKET_CLOSE:
      return {
        ...state,
        _isCurrentTicketClose: true,
        tickets: action.tickets,
      };
    case Actions.SET_MODAL_STEP:
      return {
        ...state,
        _modalState: action.step,
      };
    case Actions.BULK_CLOSE_START:
      return {
        ...state,
        tickets: state.tickets.map((ticket: Ticket) => {
          return {
            ...ticket,
            _isClosing: true,
            _isClosingError: false,
            _isClosingSuccess: false,
          };
        }),
      };
    case Actions.BULK_CLOSE_ERROR:
      return {
        ...state,
        tickets: state.tickets.map((ticket: Ticket) => {
          if (ticket.ticketId !== action.ticketId) {
            return ticket;
          }
          return {
            ...ticket,
            _isClosing: false,
            _isClosingError: true,
            _isClosingSuccess: false,
          };
        }),
      };
    case Actions.BULK_CLOSE_SUCCESS:
      return {
        ...state,
        _totalClosed: state._totalClosed + 1,
        tickets: state.tickets.map((ticket: Ticket) => {
          if (ticket.ticketId !== action.ticketId) {
            return ticket;
          }
          return {
            ...ticket,
            _isClosing: false,
            _isClosingError: false,
            _isClosingSuccess: true,
          };
        }),
      };
    default:
      return state;
  }
};

/* Thunks */

const closeTickets = (tickets: TicketArray) => async (
  dispatch: Dispatch<BulkCloseAction>,
  getState: Function,
) => {
  // Track close ticket errors to update state in bulk
  let errors: TicketArray = [];

  const onError = (ticket: Ticket) => () => {
    dispatch(setTicketClosingError(ticket.ticketId));
    errors.push({
      ...ticket,
      _isClosing: false,
      _isClosingError: true,
      _isClosingSuccess: false,
    });
  };

  const onSuccess = (ticket: Ticket) => () => {
    dispatch(setTicketClosingSuccess(ticket.ticketId));
    dispatch(toggleTicketCheck(ticket.ticketId));
    dispatch(deleteUpdateTicketDraft(ticket.ticketId));
  };

  dispatch(setTicketClosing());

  await Promise.all(
    tickets.map((ticket) =>
      ApiRequest.put(
        `${TICKETS_URL}/${ticket.ticketId}`,
        { status: 'Closed' },
        { onError: onError(ticket), onSuccess: onSuccess(ticket) },
      ),
    ),
  );

  if (errors.length > 0) {
    dispatch(setBulkCloseTickets(errors));
    dispatch(setModalStep(MODAL_STATES.ERROR));
  } else {
    dispatch(setModalStep(MODAL_STATES.SUCCESS));
    const selectedTicketId = (getState() as AppState).selectedTicket.ticketId;

    const selectedClosed = tickets.find((item) => {
      return item.ticketId === selectedTicketId;
    });

    if (selectedClosed) {
      dispatch(getTicket(selectedTicketId, true));
    }
  }

  return dispatch(refreshCollection(COLLECTIONS.TICKET));
};

export {
  bulkCloseReducer,
  closeTickets,
  removeTicket,
  resetBulkClose,
  setBulkCloseTickets,
  setCurrentTicketClose,
  setModalStep,
  setTicketClosing,
  setTicketClosingError,
  setTicketClosingSuccess,
  showBulkCloseModal,
};
